// Pure logic shared by the Journey Builder (Add Journey and Edit Journey):
// per-place visit dates, the journey dates they give, where a new place goes,
// and how a place is named on its card. No React and no I/O.
//
// Every place has its own visit date. The journey's Start Date is the first of
// them and its End Date the last — never typed separately. The server keeps the
// same rule (route-planning syncJourneyDates), widened to recorded visits.

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(value) {
  if (!ISO_DATE.test(value || "")) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

export const PLACE_DATE_REQUIRED = "Select the visit date for this place.";

// The visit date of a place: required, a real calendar date.
export function validatePlaceDate(value) {
  if (!value) return PLACE_DATE_REQUIRED;
  if (!isIsoDate(value)) return "Enter a valid visit date.";
  return null;
}

// { startDate, endDate } from the places' visit dates ("" when none has one).
export function journeyDatesFrom(places) {
  const dates = places.map((place) => place.plannedDate).filter(isIsoDate).sort();
  return dates.length ? { startDate: dates[0], endDate: dates[dates.length - 1] } : { startDate: "", endDate: "" };
}

// Where a place visited on `date` goes so the route stays in date order: after
// every place on or before that date (places added on the same day keep the
// order they were added in). Places without a date do not move it.
export function chronologicalIndex(places, date) {
  if (!isIsoDate(date)) return places.length;
  let index = 0;
  places.forEach((place, position) => {
    if (isIsoDate(place.plannedDate) && place.plannedDate <= date) index = position + 1;
  });
  return index;
}

// The latest recorded visit at each place (one visit per place), by location id.
export function latestVisitByLocation(visits) {
  const map = new Map();
  (visits || []).forEach((visit) => {
    const locationId = visit.location_id || visit.location?.id;
    const current = map.get(locationId);
    if (!current || String(visit.visit_date) > String(current.visit_date)) map.set(locationId, visit);
  });
  return map;
}

// A place card's heading: the town (or typed area), then "District, State".
export function placeTitle(place) {
  return place.city || place.label || "Place";
}

export function placeSubtitle(place) {
  return [place.district, place.state].filter(Boolean).join(", ");
}

// A route stop from the API (GET /journeys/:id route) -> a builder place.
export function placeFromRouteCity(city) {
  return {
    key: `city-${city.id}`,
    routeCityId: city.id,
    locationId: city.location_id,
    stateId: city.state_id ? String(city.state_id) : "",
    state: city.state_name || "",
    districtId: city.district_id ? String(city.district_id) : "",
    district: city.district_name || "",
    city: city.city_name || "",
    label: city.label || city.city_name || "",
    plannedDate: city.planned_date || "",
    areas: Array.isArray(city.areas) ? city.areas : [],
    latitude: Number.isFinite(city.latitude) ? city.latitude : null,
    longitude: Number.isFinite(city.longitude) ? city.longitude : null,
    visitState: city.visit_state || "not_visited",
    latestVisitId: city.latest_visit_id || null,
  };
}
